import { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useTranslation } from 'react-i18next'
import { alpha, useTheme } from '@mui/material/styles'

import type { DayAvailability } from '../../types/api'
import { useFormatters } from '../../hooks/useFormatters'

export interface DateRange {
  checkIn: string | null
  checkOut: string | null
}

interface Props {
  days: DayAvailability[]
  value: DateRange
  onChange: (range: DateRange) => void
  monthsToShow?: number
  loading?: boolean
}

/** ISO date string for a Date, in local time. `toISOString` would shift across midnight. */
function toIso(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function addMonths(date: Date, count: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + count, 1)
}

/**
 * The date picker guests actually use.
 *
 * Written rather than taken from a library, for one reason: every off-the-shelf range picker
 * treats availability as a boolean disabled flag, and this calendar has to show a price per
 * night as well. Guests choosing a week in July are comparing 78 EUR nights against 140 EUR
 * festival nights, and hiding that until the summary is how a booking gets abandoned.
 *
 * Selection is half-open, matching the API: clicking 1 and 4 July books three nights and
 * leaves the fourth free. The departure day is drawn as an endpoint, not as a booked night.
 *
 * Weeks start on Monday because every country in this app's launch set does, and the grid is
 * built from the visitor's own locale for weekday names so Hebrew and Finnish both read
 * correctly without a table of translated day names.
 */
export function AvailabilityCalendar({ days, value, onChange, monthsToShow = 2, loading }: Props) {
  const { t } = useTranslation()
  const theme = useTheme()
  const { money, monthYear, weekdayNarrow } = useFormatters()
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()))

  const byDate = useMemo(() => {
    const map = new Map<string, DayAvailability>()
    for (const day of days) map.set(day.date, day)
    return map
  }, [days])

  const weekdayLabels = useMemo(() => {
    // 2024-01-01 was a Monday; any Monday works as the anchor.
    const monday = new Date(2024, 0, 1)
    return Array.from({ length: 7 }, (_, index) =>
      weekdayNarrow(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + index)),
    )
  }, [weekdayNarrow])

  const handleClick = (iso: string) => {
    const { checkIn, checkOut } = value

    // No selection, or a complete one: start over from this day.
    if (!checkIn || checkOut) {
      onChange({ checkIn: iso, checkOut: null })
      return
    }
    // Clicking the arrival day again, or anything before it, moves the arrival.
    if (iso <= checkIn) {
      onChange({ checkIn: iso, checkOut: null })
      return
    }
    // Refuse a range that steps over a night somebody else already has.
    if (!isRangeSelectable(checkIn, iso, byDate)) {
      onChange({ checkIn: iso, checkOut: null })
      return
    }
    onChange({ checkIn, checkOut: iso })
  }

  const months = Array.from({ length: monthsToShow }, (_, index) => addMonths(cursor, index))

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <IconButton
          onClick={() => setCursor(addMonths(cursor, -1))}
          disabled={cursor <= startOfMonth(new Date())}
          aria-label={t('calendar.previousMonth')}
        >
          {/* Chevrons are mirrored by the RTL plugin, so "left" stays "previous" in Hebrew. */}
          <ChevronLeftIcon />
        </IconButton>

        <Typography variant="h6" component="p">
          {months.map((month) => monthYear(month)).join(' · ')}
        </Typography>

        <IconButton
          onClick={() => setCursor(addMonths(cursor, 1))}
          aria-label={t('calendar.nextMonth')}
        >
          <ChevronRightIcon />
        </IconButton>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: `repeat(${monthsToShow}, 1fr)` },
          opacity: loading ? 0.45 : 1,
          transition: 'opacity 150ms ease',
          pointerEvents: loading ? 'none' : 'auto',
        }}
      >
        {months.map((month) => (
          <MonthGrid
            key={month.toISOString()}
            month={month}
            weekdayLabels={weekdayLabels}
            byDate={byDate}
            value={value}
            onDayClick={handleClick}
            monthLabel={monthYear(month)}
            money={money}
          />
        ))}
      </Box>

      <Stack
        direction="row"
        spacing={2.5}
        sx={{ mt: 2.5, flexWrap: 'wrap', rowGap: 1, alignItems: 'center' }}
      >
        <LegendSwatch color={theme.palette.background.paper} border label={t('calendar.available')} />
        <LegendSwatch color={alpha(theme.palette.text.primary, 0.09)} label={t('calendar.unavailable')} />
        <LegendSwatch color={theme.palette.primary.main} label={t('calendar.selected')} />

        {(value.checkIn || value.checkOut) && (
          <Button size="small" onClick={() => onChange({ checkIn: null, checkOut: null })}>
            {t('calendar.clearDates')}
          </Button>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {value.checkIn && !value.checkOut ? t('calendar.chooseCheckOut') : t('calendar.legend')}
      </Typography>
    </Box>
  )
}

/** True when every night in [from, to) is bookable. */
function isRangeSelectable(from: string, to: string, byDate: Map<string, DayAvailability>): boolean {
  const cursor = new Date(`${from}T00:00:00`)
  const end = new Date(`${to}T00:00:00`)
  while (cursor < end) {
    const day = byDate.get(toIso(cursor))
    if (day && !day.available) return false
    cursor.setDate(cursor.getDate() + 1)
  }
  return true
}

function MonthGrid({
  month,
  weekdayLabels,
  byDate,
  value,
  onDayClick,
  monthLabel,
  money,
}: {
  month: Date
  weekdayLabels: string[]
  byDate: Map<string, DayAvailability>
  value: DateRange
  onDayClick: (iso: string) => void
  monthLabel: string
  money: (value: number | null | undefined) => string
}) {
  const theme = useTheme()
  const todayIso = toIso(new Date())

  const firstOfMonth = new Date(month.getFullYear(), month.getMonth(), 1)
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
  // getDay() is 0 for Sunday; shift so Monday is column 0.
  const leadingBlanks = (firstOfMonth.getDay() + 6) % 7

  return (
    <Box role="group" aria-label={monthLabel}>
      <Typography
        variant="subtitle2"
        sx={{ display: { xs: 'block', md: 'none' }, mb: 1, fontWeight: 600 }}
      >
        {monthLabel}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
        {weekdayLabels.map((label, index) => (
          <Typography
            key={index}
            variant="caption"
            align="center"
            sx={{ color: 'text.secondary', fontWeight: 600, pb: 0.5 }}
          >
            {label}
          </Typography>
        ))}

        {Array.from({ length: leadingBlanks }).map((_, index) => (
          <Box key={`blank-${index}`} />
        ))}

        {Array.from({ length: daysInMonth }, (_, index) => {
          const date = new Date(month.getFullYear(), month.getMonth(), index + 1)
          const iso = toIso(date)
          const day = byDate.get(iso)

          const past = iso < todayIso
          // A night with no row is available at the base rate, exactly as the API defines it.
          const available = !past && (day?.available ?? true)

          const isCheckIn = value.checkIn === iso
          const isCheckOut = value.checkOut === iso
          const inRange =
            value.checkIn != null && value.checkOut != null && iso > value.checkIn && iso < value.checkOut

          const selected = isCheckIn || isCheckOut || inRange

          return (
            <Box
              key={iso}
              component="button"
              type="button"
              disabled={!available}
              onClick={() => onDayClick(iso)}
              aria-label={`${iso}${available ? '' : ' — unavailable'}`}
              aria-pressed={selected}
              sx={{
                appearance: 'none',
                border: 0,
                cursor: available ? 'pointer' : 'default',
                font: 'inherit',
                p: 0.25,
                minHeight: 52,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1.15,
                transition: 'background-color 120ms ease, color 120ms ease',
                bgcolor: selected
                  ? isCheckIn || isCheckOut
                    ? 'primary.main'
                    : alpha(theme.palette.primary.main, 0.16)
                  : available
                    ? 'transparent'
                    : alpha(theme.palette.text.primary, 0.06),
                color: selected && (isCheckIn || isCheckOut)
                  ? 'primary.contrastText'
                  : available
                    ? 'text.primary'
                    : 'text.disabled',
                textDecoration: available ? 'none' : 'line-through',
                outline: iso === todayIso ? `1px solid ${theme.palette.primary.main}` : 'none',
                '&:hover': available && !selected ? { bgcolor: alpha(theme.palette.primary.main, 0.08) } : {},
                '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
              }}
            >
              <Box component="span" sx={{ fontWeight: selected ? 700 : 500, fontSize: '0.9rem' }}>
                {index + 1}
              </Box>
              {available && day?.price != null && (
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.65rem',
                    opacity: selected && (isCheckIn || isCheckOut) ? 0.9 : 0.62,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {money(day.price)}
                </Box>
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

function LegendSwatch({
  color,
  label,
  border,
}: {
  color: string
  label: string
  border?: boolean
}) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: 1,
          bgcolor: color,
          border: border ? '1px solid' : 'none',
          borderColor: 'divider',
        }}
      />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  )
}
